"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, Zap, Lock, EyeOff, Globe, Server, Wifi, Key,
  Cpu, HardDrive, RefreshCw, GitBranch, Cloud, Download,
  Monitor, Users, MapPin, Clock, Layers, CheckCircle, Eye,
  shield: Shield, zap: Zap, lock: Lock, "eye-off": EyeOff,
  globe: Globe, server: Server, wifi: Wifi, key: Key,
  cpu: Cpu, "hard-drive": HardDrive, "refresh-cw": RefreshCw,
  "git-branch": GitBranch, cloud: Cloud, download: Download,
  monitor: Monitor, users: Users, "map-pin": MapPin,
  clock: Clock, layers: Layers, "check-circle": CheckCircle, eye: Eye,
};

interface BentoItem {
  title: string;
  description: string;
  icon?: string;
  span?: "sm" | "md" | "lg";
}

interface BentoGridProps {
  title?: string;
  subtitle?: string;
  items: BentoItem[];
}

const BentoGrid = ({ title, subtitle, items }: BentoGridProps) => {
  // Default placeholder items if none provided
  const defaultItems: BentoItem[] = [
    {
      title: "Military-Grade Encryption",
      description: "AES-256 encryption protects your data from hackers and surveillance.",
      icon: "shield",
      span: "lg"
    },
    {
      title: "Lightning Fast Speeds",
      description: "Experience minimal speed loss with our 10Gbps servers.",
      icon: "zap"
    },
    {
      title: "Global Server Network",
      description: "Connect to 4,500+ servers in 100+ countries worldwide.",
      icon: "globe"
    },
    {
      title: "No-Logs Policy",
      description: "We don't track, collect, or share your online activity. Ever.",
      icon: "eye-off"
    },
    {
      title: "24/7 Support",
      description: "Get help anytime with our live chat support team.",
      icon: "users"
    }
  ];

  const displayItems = (!items || items.length === 0) ? defaultItems : items;
  const displayTitle = title || "Why Choose Tiera VPN";
  const displaySubtitle = subtitle || "Premium features designed for your privacy and security";

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 space-y-4">
          <BlurFade delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {displayTitle}
            </h2>
          </BlurFade>
          <BlurFade delay={0.15}>
            <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
              {displaySubtitle}
            </p>
          </BlurFade>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {displayItems.map((item, i) => {
            const Icon = item.icon ? (ICON_MAP[item.icon] || Shield) : null;
            const spanClass =
              item.span === "lg"
                ? "md:col-span-2"
                : "md:col-span-1";

            return (
              <BlurFade key={i} delay={0.15 + i * 0.05}>
                <Card
                  className={`${spanClass} border-foreground/5 bg-muted/30 hover:bg-muted/60 transition-colors h-full`}
                >
                  <CardContent className="p-6 md:p-8 space-y-3">
                    {Icon && (
                      <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Icon size={22} className="text-primary" strokeWidth={1.5} />
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-foreground">
                      {item.title}
                    </h3>
                    <p className="text-foreground/60 leading-relaxed text-sm">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BentoGrid;
